/**
 * 统一 fetch 客户端。
 *
 * 职责：
 *  - 统一加 Authorization / X-Request-Id / Content-Type
 *  - 自动带 cookie（refresh 走 HttpOnly cookie，详见 ADR-0010）
 *  - 401 触发一次 refresh，成功后重放原请求；二次 401 → 清登录态 + 跳 /login
 *  - 错误统一映射为 ApiError，组件按 code 分支处理（[.ai/rules/frontend.md §7]）
 *
 * 为什么不用 axios：
 *  - 不在 ADR 中授权
 *  - fetch + AbortController + 简单 wrapper 已能覆盖 V1.0 需求
 */
import type { ApiError, ApiErrorResponse } from '@dental/shared-types';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const API_PREFIX = '/api';

export class ApiHttpError extends Error {
  readonly code: string;
  readonly status: number;
  // `exactOptionalPropertyTypes: true` 下 readonly `?: T` 不接受 undefined 赋值；
  // 显式列 `| undefined` 让 payload.* 可能为 undefined 也能赋。
  readonly details?: Record<string, unknown> | undefined;
  readonly requestId?: string | undefined;

  constructor(status: number, payload: ApiError) {
    super(payload.message || 'API error');
    this.name = 'ApiHttpError';
    this.code = payload.code;
    this.status = status;
    this.details = payload.details;
    this.requestId = payload.requestId;
  }
}

type ApiInit = Omit<RequestInit, 'body'> & {
  body?: unknown; // 自动 JSON.stringify
  /** 跳过 401 自动 refresh（refresh 接口本身用） */
  skipAutoRefresh?: boolean;
};

let accessTokenGetter: () => string | null = () => null;
let refreshHandler: (() => Promise<boolean>) | null = null;
let onAuthFailure: (() => void) | null = null;
let inflightRefresh: Promise<boolean> | null = null;

/**
 * App 启动时由 RootProviders 注入。
 *
 * 之所以做成 setter 而不是直接 import store：
 *  - 避免 services 依赖 React 模块（services 应当可 ssr / 可独立测试）
 *  - 单测时可以直接注入 mock
 */
export function bindApiAccessToken(getter: () => string | null): void {
  accessTokenGetter = getter;
}

export function bindApiRefreshHandler(handler: () => Promise<boolean>): void {
  refreshHandler = handler;
}

export function bindApiAuthFailureHandler(handler: () => void): void {
  onAuthFailure = handler;
}

function buildHeaders(init: ApiInit): Headers {
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('X-Request-Id')) {
    headers.set('X-Request-Id', generateRequestId());
  }
  const token = accessTokenGetter();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

function generateRequestId(): string {
  // 不依赖 crypto.randomUUID 以兼容老浏览器；强度足够 trace
  return 'req_' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
}

async function request<T>(method: string, path: string, init: ApiInit = {}): Promise<T> {
  const url = `${BASE_URL}${API_PREFIX}${path}`;
  // exactOptionalPropertyTypes: true 下 RequestInit.body 不接受 undefined，
  // 只在有 body 时把字段加进 fetchInit。spread 时排除 ApiInit-only 字段
  // （body: unknown / skipAutoRefresh）避免 RequestInit 类型不匹配。
  const buildFetchInit = (): RequestInit => {
    const { body: _customBody, skipAutoRefresh: _skip, ...restInit } = init;
    void _customBody;
    void _skip;
    const fetchInit: RequestInit = {
      ...restInit,
      method,
      headers: buildHeaders(init),
      credentials: 'include',
    };
    if (init.body !== undefined) {
      fetchInit.body = JSON.stringify(init.body);
    }
    return fetchInit;
  };

  let response = await fetch(url, buildFetchInit());

  if (response.status === 401 && !init.skipAutoRefresh && refreshHandler) {
    const ok = await tryRefreshOnce();
    if (ok) {
      response = await fetch(url, buildFetchInit());
    }
  }

  if (!response.ok) {
    const payload = await readErrorPayload(response);
    if (response.status === 401 && !init.skipAutoRefresh) {
      onAuthFailure?.();
    }
    throw new ApiHttpError(response.status, payload);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return text.length === 0 ? (undefined as T) : (JSON.parse(text) as T);
}

async function tryRefreshOnce(): Promise<boolean> {
  if (!refreshHandler) return false;
  if (!inflightRefresh) {
    inflightRefresh = refreshHandler().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

async function readErrorPayload(response: Response): Promise<ApiError> {
  try {
    const text = await response.text();
    if (!text) return fallbackError(response);
    const json = JSON.parse(text) as Partial<ApiErrorResponse>;
    if (json.error && typeof json.error === 'object') return json.error;
    return fallbackError(response);
  } catch {
    return fallbackError(response);
  }
}

function fallbackError(response: Response): ApiError {
  return {
    code: response.status === 401 ? 'AUTH_TOKEN_INVALID' : 'INTERNAL_ERROR',
    message:
      response.status === 401
        ? '登录态无效或已过期，请重新登录'
        : `请求失败（HTTP ${response.status}）`,
  };
}

export const api = {
  get: <T>(path: string, init?: ApiInit) => request<T>('GET', path, init),
  post: <T>(path: string, body?: unknown, init?: ApiInit) =>
    request<T>('POST', path, { ...init, body }),
  patch: <T>(path: string, body?: unknown, init?: ApiInit) =>
    request<T>('PATCH', path, { ...init, body }),
  put: <T>(path: string, body?: unknown, init?: ApiInit) =>
    request<T>('PUT', path, { ...init, body }),
  delete: <T>(path: string, init?: ApiInit) => request<T>('DELETE', path, init),
};
